import { type CreditEntry, type CreditSummary, rupees, kindLabel, fmtDate } from "@/lib/cohort-credits";

export const L = {
  panel: "bg-[#FAF8F4] text-[#1F1B16] dark:bg-[#17140F] dark:text-[#F2EDE4]",
  sub: "bg-[#F1EDE5] dark:bg-[#211D17]",
  border: "border-[#D9D1C3] dark:border-[#3A332A]",
  muted: "text-[#5E5548] dark:text-[#B9AE9C]",
  input: "bg-[#FFFDF9] text-[#1F1B16] border border-[#C9BFAE] placeholder:text-[#8A8072] dark:bg-[#0F0D0A] dark:text-[#F2EDE4] dark:border-[#4A4136] dark:placeholder:text-[#857A69] focus:outline-none focus:ring-2 focus:ring-[#FF5C00] rounded-lg px-3 py-2 text-sm w-full",
  primary: "bg-[#C64600] hover:bg-[#A83B00] text-[#FFF8F2] disabled:bg-[#BFB5A6] disabled:text-[#4A4136] dark:disabled:bg-[#3A332A] dark:disabled:text-[#9C917F] disabled:cursor-not-allowed",
  ghost: "bg-[#FFFDF9] hover:bg-[#EDE7DC] text-[#1F1B16] border border-[#C9BFAE] dark:bg-[#211D17] dark:hover:bg-[#2C261E] dark:text-[#F2EDE4] dark:border-[#4A4136] disabled:opacity-60",
  warn: "bg-[#FCEBC7] text-[#6B4300] border-[#E8C277] dark:bg-[#3A2A0C] dark:text-[#F5D38A] dark:border-[#7A5A1C]",
  error: "bg-[#F8DAD6] text-[#7A1D12] border-[#E5A39A] dark:bg-[#3D1712] dark:text-[#F4B3A9] dark:border-[#7D3328]",
  ok: "bg-[#DDF1E4] text-[#14532D] border-[#9BCFAE] dark:bg-[#12301F] dark:text-[#A7E3BC] dark:border-[#2B6B45]",
};

export function SummaryGrid({ s }: { s: CreditSummary }) {
  const items: [string, number, boolean?][] = [
    ["Available to allocate", s.remainingAvailable, true],
    ["Money received", s.paymentReceived],
    ["Included Allocation Credit", s.includedCredit],
    ["Favcy Added Allocation Credit", s.addedCredit],
    ["Conditional (not yet usable)", s.conditionalCredit],
    ["Included applied", s.appliedIncluded],
    ["Added applied", s.appliedAdded],
    ["Included remaining", s.remainingIncluded],
    ["Added remaining", s.remainingAdded],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="grid-credit-summary">
      {items.map(([label, v, hero]) => (
        <div key={label} className={`rounded-xl border p-3 ${hero ? "border-[#C64600] bg-[#FDEBDD] dark:bg-[#3A1F0E] col-span-2 md:col-span-1" : `${L.border} bg-[#FFFDF9] dark:bg-[#1C1914]`}`}>
          <p className={`text-[10px] uppercase tracking-wider font-mono ${L.muted}`}>{label}</p>
          <p className="text-lg font-bold font-mono mt-0.5 break-all" data-testid={`text-summary-${label.replace(/\W+/g, "-").toLowerCase()}`}>{rupees(v)}</p>
        </div>
      ))}
    </div>
  );
}

export function HistoryTable({ entries, companies, onRelease }: { entries: CreditEntry[]; companies?: { id: number; name: string }[]; onRelease?: (e: CreditEntry) => void }) {
  if (!entries.length) return <div className={`rounded-xl border ${L.border} ${L.sub} p-6 text-center text-sm ${L.muted}`}>No transactions recorded for this cohort yet.</div>;
  const company = (id: number | null) => companies?.find(c => c.id === id)?.name || (id ? `Company ${id}` : "");
  const cell = (n: number) => n ? rupees(n) : "—";
  return (
    <div className={`border ${L.border} rounded-xl overflow-x-auto [&_table]:min-w-[1050px] [&_th]:whitespace-nowrap [&_td]:[overflow-wrap:normal]`}>
      <table className="w-full text-sm min-w-[880px]">
        <thead className={`${L.sub} text-[11px] uppercase tracking-wider ${L.muted}`}>
          <tr>{["Date", "Reference", "Type", "Money received", "Included", "Added", "Conditional", "Applied", "Details", ""].map(h => <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-[#D9D1C3] dark:divide-[#3A332A]">
          {entries.map(e => (
            <tr key={e.id} data-testid={`row-credit-${e.id}`}>
              <td className="px-3 py-2 whitespace-nowrap">{fmtDate(e.date)}</td>
              <td className="px-3 py-2 font-mono text-xs">{e.reference}</td>
              <td className="px-3 py-2">{kindLabel(e.kind)}</td>
              <td className="px-3 py-2 font-mono">{cell(e.payment)}</td>
              <td className="px-3 py-2 font-mono">{cell(e.included)}</td>
              <td className="px-3 py-2 font-mono">{cell(e.added)}</td>
              <td className="px-3 py-2 font-mono">{cell(e.conditional)}{e.released && <span className="block text-xs">Released</span>}</td>
              <td className="px-3 py-2 font-mono text-xs">{e.appliedIncluded || e.appliedAdded ? <>{rupees(e.appliedIncluded)} incl.<br />{rupees(e.appliedAdded)} added</> : "—"}</td>
              <td className={`px-3 py-2 text-xs ${L.muted} max-w-[220px]`}>
                {company(e.companyId) && <p className="font-semibold text-[#1F1B16] dark:text-[#F2EDE4]">{company(e.companyId)}</p>}
                {e.conditions && <p>Condition: {e.conditions}</p>}
                {e.notes && <p>{e.notes}</p>}
              </td>
              <td className="px-3 py-2 text-right">
                {onRelease && e.conditional > 0 && !e.released && <button onClick={() => onRelease(e)} className={`text-xs px-2.5 py-1.5 rounded-lg ${L.ghost}`} data-testid={`button-release-${e.id}`}>Release</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
